/**
 * Error Handler Utility
 *
 * Backend hatalarını kullanıcı dostu mesajlara çevirir (i18n - 6 locale).
 * Tüm API hatalarını merkezi bir noktadan yönetir.
 */

import { getTranslation } from '../hooks/useTranslation';

/**
 * observability: generic-fallback (5xx + taninmayan) hatalari konsola logla.
 * Kullaniciya donen mesaj DEGISMEZ; gercek neden maskelenmesin diye ek log.
 */
const _logDiag = (error) => {
  console.error('[errorHandler] generic-fallback', {
    status: error?.response?.status,
    code: error?.code,
    url: error?.config?.url,
    method: error?.config?.method,
    message: error?.message,
    detail: error?.response?.data?.detail
  });
};

/**
 * Backend stabil hata kodu -> i18n key haritasi.
 * Sol: backend'in donderdigi stabil kod (response.data.code). Sag: locale i18n key.
 * Yeni kod-dali (asagida) bunlari t(key, params) ile cevirir.
 */
const ERROR_CODE_MAP = {
  // Commit 1 - AUTH pilot (login + 2FA)
  'auth.login.locked': 'auth.login.locked',
  'auth.invalid_credentials': 'auth.login.error_credentials',                    // mevcut key reuse
  'auth.invalid_credentials_remaining': 'auth.login.error_credentials_remaining',
  'auth.login.email_not_verified': 'auth.login.email_not_verified',
  'auth.login.inactive': 'auth.login.inactive',
  'auth.login.error_2fa_wrong': 'auth.login.error_2fa_wrong',                    // mevcut key reuse

  // Commit 2a - DESEN C (reset / verify / contact HTTPException errors)
  'auth.reset.token_invalid': 'auth.reset.token_invalid_toast',                  // mevcut key reuse
  'auth.reset.token_expired': 'auth.reset.token_expired',
  'auth.password.weak': 'auth.reset.validation_requirements',                    // mevcut key reuse
  'auth.reset.password_reused': 'auth.reset.password_reused',
  'auth.user_not_found': 'errors.user_not_found',
  'auth.verify.invalid_link': 'auth.verify.invalid_link',                        // mevcut key reuse
  'auth.verify.error': 'auth.verify.error',                                      // mevcut key reuse
  'auth.verify.email_required': 'emailverify.error_no_email',                    // mevcut key reuse
  'auth.verify.send_error': 'emailverify.resend_error',                          // mevcut key reuse
  'contact.email_send_error': 'contact.message_failed',                          // mevcut key reuse
  'contact.generic_error': 'contact.generic_error',                             // mevcut key reuse

  // Register/reset password policy (5 rules) - code == key, mevcut checklist label'lari reuse
  'auth.password.min_length': 'auth.password.min_length',
  'auth.password.uppercase': 'auth.password.uppercase',
  'auth.password.lowercase': 'auth.password.lowercase',
  'auth.password.number': 'auth.password.number',
  'auth.password.special': 'auth.password.special',

  // C3-pilot - team-invite (3 endpoint: invite / cancel / remove)
  'auth.unauthorized': 'errors.session_expired',                    // mevcut key reuse
  'team.admin_only': 'errors.team.admin_only',
  'team.pro_business_only': 'errors.team.pro_business_only',
  'team.no_team_id': 'errors.team.no_team_id',
  'team.email_exists': 'errors.team.email_exists',
  'team.already_invited': 'errors.team.already_invited',
  'team.invite_not_found': 'errors.team.invite_not_found',
  'team.only_pending_cancellable': 'errors.team.only_pending_cancellable',
  'team.member_not_found': 'errors.team.member_not_found',
  'team.not_in_team': 'errors.team.not_in_team',
  'team.cannot_remove_admin': 'errors.team.cannot_remove_admin',
  'team.min_credits_required': 'errors.team.min_credits_required',
  'team.max_users_reached': 'errors.team.max_users_reached',

  // C3a-1 - 2FA (setup / verify / disable)
  'auth.2fa.already_enabled': 'errors.2fa.already_enabled',
  'auth.2fa.setup_first': 'errors.2fa.setup_first',
  'auth.2fa.already_disabled': 'errors.2fa.already_disabled',

  // C3a-2 - Google OAuth + token-login
  'auth.session_invalid': 'errors.session_invalid',
  'auth.session_expired': 'errors.session_expired',                  // mevcut key reuse
  'auth.service_timeout': 'errors.service_timeout',
  'auth.service_unavailable': 'errors.service_unavailable',
  'auth.google.start_failed': 'errors.google.start_failed',
  'auth.must_accept_terms': 'auth.google.must_accept_terms',         // mevcut key reuse
  'auth.internal_error': 'errors.server_error',                      // mevcut key reuse

  // C3a-3 - register + token/refresh
  'auth.register.disabled': 'errors.register.disabled',
  'auth.register.not_whitelisted': 'errors.register.not_whitelisted',
  'auth.email_exists': 'errors.register.email_exists',
  'auth.token_invalid': 'errors.token_invalid',
  'auth.refresh_token_missing': 'errors.refresh_token_missing',

  // C3b - profile + subscription
  'profile.invalid_profile_type': 'errors.profile.invalid_profile_type',
  'profile.current_password_required': 'errors.profile.current_password_required',
  'profile.current_password_wrong': 'errors.profile.current_password_wrong',
  'profile.no_fields_to_update': 'errors.profile.no_fields_to_update',
  'subscription.only_monthly_cancellable': 'errors.subscription.only_monthly_cancellable',
  'subscription.already_cancelled': 'errors.subscription.already_cancelled',

  // C3c - report
  'report.min_one_profile': 'errors.report.min_one_profile',
  'report.selected_not_found': 'errors.report.selected_not_found',

  // C3e - team-invite (public) + query + verify (REUSE: team.invite_not_found,
  // auth.email_exists, auth.user_not_found yukarida zaten var)
  'team.invite_expired': 'errors.team.invite_expired',
  'team.invite_used': 'errors.team.invite_used',
  'team.invite_admin_not_found': 'errors.team.invite_admin_not_found',
  'query.bulk_deleted': 'errors.query.bulk_deleted',
  'auth.verify.already_verified': 'emailverify.already_verified',

  // C3f - verify-email success 200-body
  'auth.verify.success': 'emailverify.success_message',
};

/**
 * Backend hata kodlarını kullanıcı dostu mesajlara çevirir (i18n)
 * @param {Error} error - Axios veya fetch error objesi
 * @param {Function} [t] - useTranslation() t fonksiyonu; verilmezse stored-locale fallback
 * @param {string} [fallbackKey] - taninmayan hata icin i18n key (verilmezse 'errors.server_error')
 * @returns {string} Kullanıcıya gösterilecek (locale'e göre) hata mesajı
 */
export const getReadableErrorMessage = (error, t, fallbackKey) => {
  // i18n: caller t gecmezse getTranslation (stored locale) ile coz
  const tr = typeof t === 'function' ? t : (key) => getTranslation(key);

  // 1. Backend'den gelen detaylı hata mesajını al
  const detail = error?.response?.data?.detail;
  const statusCode = error?.response?.status;
  const errorCode = error?.response?.data?.code || error?.code;

  // 1b. Backend stabil-kod (explicit) - status/detail sezgisinden ONCE gelir.
  // ERROR_CODE_MAP simdilik BOS -> tetiklenmez (davranis-notr). params-destekli ceviri:
  // tr() wrapper (line 33) params'i dusurur; bu yuzden dogrudan t / getTranslation (ikisi
  // de (key, params) imzasini destekler) kullanilir.
  const code = error?.response?.data?.code;
  const params = error?.response?.data?.params || {};
  if (code && ERROR_CODE_MAP[code]) {
    return typeof t === 'function'
      ? t(ERROR_CODE_MAP[code], params)
      : getTranslation(ERROR_CODE_MAP[code], params);
  }

  // 2. Bilinen hata kodlarını çevir

  // Analiz Hakkı/Ödeme hataları
  if (detail === 'insufficient_credits' || statusCode === 402 || errorCode === 'insufficient_credits') {
    return tr('errors.insufficient_credits');
  }

  if (detail === 'subscription_expired' || errorCode === 'subscription_expired') {
    return tr('errors.subscription_expired');
  }

  if (detail === 'payment_required' || errorCode === 'payment_required') {
    return tr('errors.payment_required');
  }

  // Rate limiting hataları
  if (detail === 'concurrent_limit_reached' || errorCode === 'concurrent_limit_reached') {
    return tr('errors.concurrent_limit');
  }

  if (detail === 'rate_limit_exceeded' || statusCode === 429 || errorCode === 'rate_limit_exceeded') {
    return tr('errors.rate_limit');
  }

  // İçerik hataları
  if (detail === 'reel_not_found' || errorCode === 'reel_not_found') {
    return tr('errors.reel_not_found');
  }

  if (detail === 'profile_not_found' || errorCode === 'profile_not_found') {
    return tr('errors.profile_not_found');
  }

  if (detail === 'private_account' || errorCode === 'private_account') {
    return tr('errors.private_account');
  }

  // Scraping/Teknik hatalar
  if (detail?.includes?.('Scraping failed') || errorCode === 'scraping_failed') {
    return tr('errors.scraping_failed');
  }

  if (detail?.includes?.('Both BD and SF failed')) {
    return tr('errors.data_unavailable');
  }

  // Kimlik doğrulama hataları
  if (statusCode === 401 || detail === 'Not authenticated' || errorCode === 'unauthorized') {
    return tr('errors.session_expired');
  }

  // Hesap dondurma hatası (past_due)
  if (detail === 'account_frozen' || errorCode === 'account_frozen') {
    return tr('errors.account_frozen');
  }

  if (statusCode === 403 || errorCode === 'forbidden') {
    return tr('errors.forbidden');
  }

  // Sunucu hataları
  if (statusCode === 500) {
    _logDiag(error);
    return tr('errors.server_error');
  }

  if (statusCode === 502 || statusCode === 503 || statusCode === 504 || statusCode === 520) {
    _logDiag(error);
    return tr('errors.server_error');
  }

  if (statusCode === 520) {
    return tr('errors.server_error');
  }

  // Ağ hataları
  if (error?.code === 'ERR_NETWORK' || error?.message === 'Network Error') {
    return tr('errors.network');
  }

  if (error?.code === 'ECONNABORTED' || error?.message?.includes?.('timeout')) {
    return tr('errors.timeout');
  }

  // 3. Bilinmeyen hatalar için genel mesaj - teknik detay gösterme!
  _logDiag(error);
  return fallbackKey ? tr(fallbackKey) : tr('errors.server_error');
};

/**
 * Hata koduna göre toast tipini belirler (info, warning, error)
 * @param {Error} error - Axios veya fetch error objesi
 * @returns {string} Toast tipi: 'info' | 'warning' | 'error'
 */
export const getErrorSeverity = (error) => {
  const statusCode = error?.response?.status;
  const detail = error?.response?.data?.detail;
  
  // Bilgilendirme seviyesi (kullanıcı aksiyonu gerekli)
  if (detail === 'insufficient_credits' || statusCode === 402) {
    return 'warning';
  }
  
  if (detail === 'subscription_expired') {
    return 'warning';
  }
  
  // Rate limiting (geçici durum)
  if (statusCode === 429 || detail === 'rate_limit_exceeded') {
    return 'info';
  }
  
  // Gerçek hatalar
  return 'error';
};

export default {
  getReadableErrorMessage,
  getErrorSeverity
};
